import React from 'react';
import { ArrowLeft, Sun, Package, DollarSign, Grid, List, Scale, Receipt, Check, Bell, Zap, Truck, CreditCard } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Switch } from '@headlessui/react';
import { useTheme } from '../../../../contexts/ThemeContext';

const GeneralSettings = () => {
  const navigate = useNavigate();
  const { darkMode, toggleDarkMode } = useTheme();
  
  // Función helper para los switches
  const SettingSwitch = ({ enabled, onChange, disabled = false }) => (
    <Switch
      checked={enabled}
      onChange={disabled ? undefined : onChange}
      className={`${enabled ? 'bg-blue-600' : 'bg-gray-200'} 
        relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none
        ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
    >
      <span
        className={`${enabled ? 'translate-x-6' : 'translate-x-1'} 
          inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
      />
    </Switch>
  );

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="sticky top-0 bg-white dark:bg-gray-800 border-b dark:border-gray-700">
        <div className="flex items-center p-4">
          <div className="flex items-center gap-4">
            <button 
              onClick={() => navigate('/settings')}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
            >
              <ArrowLeft className="w-5 h-5 dark:text-gray-200" />
            </button>
            <h1 className="text-2xl font-bold dark:text-white">Configuración general</h1>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-4 space-y-4">
        {/* Apariencia */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm">
          <div className="p-4 border-b dark:border-gray-700">
            <h2 className="font-medium dark:text-white">Apariencia</h2>
          </div>
          <div className="p-4 space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Sun className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                <span className="dark:text-gray-200">Modo oscuro</span>
              </div>
              <SettingSwitch enabled={darkMode} onChange={toggleDarkMode} />
            </div>
            <div className="flex items-center justify-between opacity-50">
              <div className="flex items-center gap-3">
                {<List className="w-5 h-5 text-gray-600 dark:text-gray-300" />}
                <span className="dark:text-gray-200">Vista en grilla</span>
              </div>
              <SettingSwitch enabled={false} onChange={() => {}} disabled />
            </div>
          </div>
        </div>

        {/* Ventas */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm">
          <div className="p-4 border-b dark:border-gray-700">
            <h2 className="font-medium dark:text-white">Ventas</h2>
          </div>
          <div className="p-4 space-y-4">
            <div className="flex items-center justify-between opacity-50">
              <div className="flex items-center gap-3">
                <Scale className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                <span className="dark:text-gray-200">Usar tara</span>
              </div>
              <SettingSwitch enabled={false} onChange={() => {}} disabled />
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Receipt className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                <span className="dark:text-gray-200">Usar seña</span>
              </div>
              <SettingSwitch enabled={true} onChange={() => {}} disabled />
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <DollarSign className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                <span className="dark:text-gray-200">Moneda: ARS</span>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions - Simplified */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm">
          <div className="p-4 border-b dark:border-gray-700">
            <h2 className="font-medium dark:text-white">Acciones Rápidas</h2>
          </div>
          <div className="p-4 space-y-2">
            {[
              { icon: Package, title: 'Nuevo Lote' },
              { icon: DollarSign, title: 'Nueva Venta' }
            ].map(action => {
              const Icon = action.icon;
              return (
                <div key={action.title} className="flex items-center justify-between p-2">
                  <div className="flex items-center gap-3">
                    <Icon className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                    <span className="text-gray-600 dark:text-gray-300">{action.title}</span>
                  </div>
                  <Check className="w-5 h-5 text-blue-600" />
                </div>
              );
            })}
          </div>
        </div>

        {/* Notificaciones en Inicio */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm">
          <div className="p-4 border-b dark:border-gray-700">
            <h2 className="font-medium dark:text-white">Notificaciones en Inicio</h2>
          </div>
          <div className="p-4 space-y-2">
            {[
              { 
                icon: Zap, 
                title: 'Lotes relámpago sin reconciliar',
                description: 'Notificar cuando hay lotes relámpago pendientes de reconciliación'
              },
              { 
                icon: Truck, 
                title: 'Entregas pendientes',
                description: 'Notificar cuando hay entregas pendientes de realizar'
              },
              { 
                icon: CreditCard, 
                title: 'Ventas sin cobrar',
                description: 'Notificar cuando hay ventas pendientes de cobro'
              }
            ].map(notification => {
              const Icon = notification.icon;
              return (
                <div key={notification.title} className="flex items-center justify-between p-2 hover:bg-gray-50 dark:hover:bg-gray-700/50 rounded-lg transition-colors">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-1">
                      <Icon className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                      <span className="text-gray-900 dark:text-gray-100">{notification.title}</span>
                    </div>
                    <p className="text-sm text-gray-500 dark:text-gray-400 ml-8">
                      {notification.description}
                    </p>
                  </div>
                  <SettingSwitch enabled={true} onChange={() => {}} disabled />
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GeneralSettings; 